import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ProjectRouteStation } from '../../shared/interfaces/project-route-station.interface';
import { ProjectGroup } from '../../shared/interfaces/project-group.interface';
import { ProjectGroupMetaInformation } from '../../shared/interfaces/project-group-meta-information';
import { TIMER_COMPONENT_TYPES, timerTypeStyle } from '../shared/time-switch-component-types';

export interface StationAssignDialogData {
  stations: ProjectRouteStation[];
  projectGroups: ProjectGroup[];
  metaByGroup: Map<number, ProjectGroupMetaInformation[]>;
  /** Guid -> ansteuerbare Typen (nur diese werden je Station als Block angeboten). */
  capabilities: Map<string, string[]>;
  /** Guid -> aktuell zugewiesene Typen (Vorauswahl). */
  current: Map<string, string[]>;
}

export interface StationAssignDialogResult {
  assignments: { routeStationId: string, types: string[] }[];
}

/**
 * Zuweisungsdialog: zeigt ALLE Projekt-Stationen. Je Station werden nur die ansteuerbaren Typen
 * als farbige Blöcke angeboten, die einzeln an-/abgewählt werden können. Gruppen-Schnellauswahl
 * (Tri-State) wählt alle ansteuerbaren Typen aller Stationen einer Gruppe.
 */
@Component({
  selector: 'avs-station-assign-dialog',
  templateUrl: './station-assign-dialog.component.html',
  styleUrls: ['./station-assign-dialog.component.css']
})
export class StationAssignDialogComponent {
  /** Guid -> ausgewählte Typen. */
  public selected = new Map<string, Set<string>>();

  constructor(
    public dialogRef: MatDialogRef<StationAssignDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: StationAssignDialogData
  ) {
    for (const station of data.stations) {
      const drivable = new Set(this.drivableTypes(station));
      const current = (data.current?.get(station.id) ?? []).filter(t => drivable.has(t));
      this.selected.set(station.id, new Set(current));
    }
  }

  public drivableTypes(station: ProjectRouteStation): string[] {
    const types = this.data.capabilities?.get(station.id) ?? [];
    return TIMER_COMPONENT_TYPES.filter(t => types.includes(t));
  }

  public typeColor(type: string): { color: string, text: string } {
    return timerTypeStyle(type);
  }

  public metaFor(station: ProjectRouteStation): ProjectGroupMetaInformation[] | undefined {
    return this.data.metaByGroup?.get(station.groupId);
  }

  public isTypeSelected(station: ProjectRouteStation, type: string): boolean {
    return this.selected.get(station.id)?.has(type) ?? false;
  }

  public toggleType(station: ProjectRouteStation, type: string): void {
    const set = this.selected.get(station.id) ?? new Set<string>();
    if (set.has(type)) { set.delete(type); } else { set.add(type); }
    this.selected.set(station.id, set);
  }

  /** Gruppen, die im Dialog Stationen anzeigen (Reihenfolge der Projektgruppen). */
  public get displayGroups(): ProjectGroup[] {
    return this.data.projectGroups.filter(g => this.data.stations.some(s => s.groupId === g.id));
  }

  /** Alle Stationen einer Gruppe (für die gruppierte Anzeige). */
  public stationsInGroup(group: ProjectGroup): ProjectRouteStation[] {
    return this.data.stations.filter(s => s.groupId === group.id);
  }

  /** Ansteuerbare Stationen einer Gruppe (für die Tri-State-Schnellauswahl). */
  public stationsForGroup(group: ProjectGroup): ProjectRouteStation[] {
    return this.data.stations.filter(s => s.groupId === group.id && this.drivableTypes(s).length > 0);
  }

  public isGroupSelected(group: ProjectGroup): boolean {
    const stations = this.stationsForGroup(group);
    return stations.length > 0 && stations.every(s => this.allSelected(s));
  }

  public isGroupPartial(group: ProjectGroup): boolean {
    const stations = this.stationsForGroup(group);
    const withSelection = stations.filter(s => (this.selected.get(s.id)?.size ?? 0) > 0).length;
    return withSelection > 0 && !stations.every(s => this.allSelected(s));
  }

  private allSelected(station: ProjectRouteStation): boolean {
    const drivable = this.drivableTypes(station);
    const set = this.selected.get(station.id);
    return drivable.length > 0 && !!set && drivable.every(t => set.has(t));
  }

  public onGroupChanged(group: ProjectGroup, checked: boolean): void {
    this.stationsForGroup(group).forEach(s => {
      this.selected.set(s.id, checked ? new Set(this.drivableTypes(s)) : new Set<string>());
    });
  }

  public selectAll(): void {
    this.data.stations.forEach(s => this.selected.set(s.id, new Set(this.drivableTypes(s))));
  }

  public clear(): void {
    this.data.stations.forEach(s => this.selected.set(s.id, new Set<string>()));
  }

  public get selectedStationCount(): number {
    return this.data.stations.filter(s => (this.selected.get(s.id)?.size ?? 0) > 0).length;
  }

  public trackByStation(_: number, station: ProjectRouteStation): string { return station.id; }
  public trackByGroup(_: number, group: ProjectGroup): number { return group.id; }

  public cancel(): void {
    this.dialogRef.close();
  }

  public confirm(): void {
    const assignments = this.data.stations.map(s => ({
      routeStationId: s.id,
      types: [...(this.selected.get(s.id) ?? new Set<string>())]
    }));
    this.dialogRef.close({ assignments } as StationAssignDialogResult);
  }
}
